import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { BcitiContextService } from 'src/app/global/services/context/context.abstract.service';
import { UserService } from '../../service/user.abstract.service';
import { UserCard, UserRecordTransformedUser } from '../../user.model';
import { CardConfiguration } from '@bciti/types';
import { UserCardDeleteErrorDialogComponent, UserCardDeleteErrorDialogData } from '../user-card-delete-error-dialog/user-card-delete-error-dialog.component';

interface UserCardModified extends UserCard {
    proofOfIdentity: string[];
}

@Component({
    selector: 'app-user-card-delete-dialog',
    templateUrl: './user-card-delete-dialog.component.html',
    styleUrls: ['./user-card-delete-dialog.component.scss'],
})
export class UserCardDeleteDialogComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    loading = false;

    cardConfig: CardConfiguration;

    currentLang: string;

    get cardLabel(): string {
        return this.cardConfig?.identity?.label[this.currentLang];
    }

    get cardIcon(): string {
        return this.cardConfig?.identity?.icon || 'icon-my-card';
    }

    constructor(
        private translate: TranslateService,
        private dialogRef: MatDialogRef<UserCardDeleteDialogComponent>,
        private service: UserService,
        private messageService: MessageService,
        @Inject('BCITI_CONTEXT_SERVICE') private contextService: BcitiContextService,
        @Inject(MAT_DIALOG_DATA) public data: { user: UserRecordTransformedUser; card: UserCardModified },
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        combineLatest([this.contextService.getLanguage(), this.contextService.getOrganisationConfiguration()])
            .pipe(
                map(([lang, orgConfig]) => {
                    this.cardConfig = orgConfig?.support?.cards?.find((c) => c.type === this.data.card.cardType);
                    this.currentLang = lang;
                }),
            )
            .subscribe();
    }

    deleteUserCard(): void {
        this.loading = true;
        this.service
            .deleteUserCard(this.data.user.id, this.data.card._id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (_response) => {
                    this.closeDialog(true);
                    this.loading = false;
                    this.messageService.success(
                        this.translate.instant('pages.userRecords.card.deleteCardDialog.message.success', { firstName: this.data.card.firstName, lastName: this.data.card.lastName }),
                    );
                },
                (error) => {
                    if (error.status === 400 && (error?.error?.cardRequirements?.length > 0 || error?.error?.subscriptionRequirements?.length > 0)) {
                        this.closeDialog();
                        this.dialog.open(UserCardDeleteErrorDialogComponent, {
                            autoFocus: false,
                            panelClass: 'cardDeleteError',
                            data: {
                                targetCard: this.data.card.cardType,
                                cardRequirements: error.error.cardRequirements,
                                subscriptionRequirements: error.error.subscriptionRequirements,
                                userName: `${this.data.user.firstName} ${this.data.user.lastName}`,
                            } as UserCardDeleteErrorDialogData,
                        });
                    } else {
                        this.loading = false;
                        this.messageService.error(this.translate.instant('pages.userRecords.card.deleteCardDialog.message.error'));
                    }
                },
            );
    }

    closeDialog(value?: boolean): void {
        this.dialogRef.close(value);
    }

    formatDate(date: string): string {
        if (!date) {
            return '';
        }
        // Extract date portion from ISO string to avoid timezone conversion issues
        let dateString = date;
        if (date.includes('T')) {
            dateString = date.split('T')[0];
        }
        // Parse date components and create Date in local timezone
        const [year, month, day] = dateString.split('-').map(Number);
        const tempDate = new Date(year, month - 1, day);
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Intl.DateTimeFormat(`${this.translate.currentLang}-CA`, options).format(tempDate);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
